/**
 * TaskDetailsMetadata Component
 *
 * Renders the task metadata details card including:
 * - Urgency
 * - Start Date, Due Date, Created On
 * - Requested By, Channel Partner, Advertiser
 * - Request Type, Request Details
 * - External link
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Card, Row, Col, Tooltip, Tag } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { startCase } from 'lodash';
import { urgencyColors } from '../../configs/task.config';
import { neutral, bg } from '../../configs/designTokens';
import '../../styles/index.scss';

const TaskDetailsMetadata = ({ currentTask }) => {
  const renderDetailItem = (label, value) => (
    <Col className="task-metadata__field">
      <Text strong className="task-metadata__label">
        {label}
      </Text>
      <Text className="task-metadata__value">
        {value || '-'}
      </Text>
    </Col>
  );

  return (
    <Card className="task-metadata__card">
      <div className="task-metadata__container">
        <Row className="task-metadata__header">
          <Text strong className="task-metadata__title">
            Details
          </Text>
          {currentTask && currentTask.linkUrl && (
            <Tooltip title={currentTask.linkUrl}>
              <LinkOutlined
                className="task-metadata__link-icon"
                onClick={() => {
                  const url = currentTask?.linkUrl;
                  if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }
                }}
              />
            </Tooltip>
          )}
        </Row>

        {/* Urgency */}
        <Col className="task-metadata__field task-metadata__field--narrow">
          <Text strong className="task-metadata__label">
            Urgency
          </Text>
          {currentTask && currentTask.urgency ? (
            <Tag
              className="task-metadata__urgency-tag"
              style={{
                backgroundColor:
                  currentTask && currentTask.urgency
                    ? urgencyColors[currentTask.urgency]
                    : 'gray',
              }}
            >
              <Row justify="center" type="flex" align="middle" className="task-metadata__urgency-content">
                <div
                  className="task-metadata__urgency-dot"
                  style={{
                    backgroundColor:
                      currentTask && currentTask.urgency === 'MEDIUM' ? neutral[900] : bg.card,
                  }}
                />
                <Text
                  strong
                  className="task-metadata__urgency-text"
                  style={{
                    color:
                      currentTask && currentTask.urgency === 'MEDIUM' ? neutral[900] : bg.card,
                  }}
                >
                  {currentTask && currentTask.urgency
                    ? startCase(currentTask.urgency.toLowerCase())
                    : '-'}
                </Text>
              </Row>
            </Tag>
          ) : (
            <Text className="task-metadata__value">{`-`}</Text>
          )}
        </Col>

        {/* Dates */}
        {renderDetailItem(
          'Start Date',
          currentTask && currentTask.startDate
            ? new Date(currentTask.startDate).toLocaleDateString()
            : null
        )}
        {renderDetailItem(
          'Due Date',
          currentTask && currentTask.dueDate
            ? new Date(currentTask.dueDate).toLocaleDateString()
            : null
        )}
        {renderDetailItem(
          'Created On',
          currentTask && currentTask.createdAt
            ? new Date(currentTask.createdAt).toLocaleDateString()
            : 'N/A'
        )}

        {/* People */}
        {renderDetailItem(
          'Requested By',
          startCase(
            currentTask &&
              currentTask.createdBy &&
              (currentTask.createdBy.name || currentTask.createdBy.username || 'Unknown')
          )
        )}
        {renderDetailItem(
          'Channel Partner',
          currentTask && currentTask.channelPartner
            ? currentTask.channelPartner.name
            : null
        )}
        {renderDetailItem(
          'Advertiser',
          currentTask && currentTask.advertiser
            ? currentTask.advertiser.name
            : null
        )}

        {/* Request Info */}
        <Col className="task-metadata__field">
          <Text strong className="task-metadata__label">
            Request Type
          </Text>
          <Text className="task-metadata__value">
            {(currentTask && currentTask.requestType) || '-'}
          </Text>
        </Col>
        {renderDetailItem(
          'Request Details',
          currentTask && currentTask.requestSubType
        )}
      </div>
    </Card>
  );
};

TaskDetailsMetadata.propTypes = {
  currentTask: PropTypes.shape({
    id: PropTypes.string,
    urgency: PropTypes.oneOf(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW']),
    startDate: PropTypes.string,
    dueDate: PropTypes.string,
    createdAt: PropTypes.string,
    createdBy: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      username: PropTypes.string,
    }),
    channelPartner: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    advertiser: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    requestType: PropTypes.string,
    requestSubType: PropTypes.string,
    linkUrl: PropTypes.string,
  }),
};

TaskDetailsMetadata.defaultProps = {
  currentTask: null,
};

export default TaskDetailsMetadata;
